import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getJournalSourceById} from '../../../../selectors/Ares/selectors';
import MotifIcon from '@ey/motif-react-wrapper/Icon';
import MotifSelect from '@ey/motif-react-wrapper/Select';
import MotifTooltip from '@ey/motif-react-wrapper/Tooltip';
import MotifOption from '@ey/motif-react-wrapper/Option';
import MotifTableWrapper from '../../BaseComponentWrappers/MotifTableWrapper';
import {labels} from '../../../../util/utils';
import * as JournalSourceActions from '../../../../actions/JournalSource/journalsourceactions.js';
import {useEngagementId} from '../../../../util/customHooks';
import styled from 'styled-components';
import ProgressLoader from '../../BaseComponentWrappers/MotifProgressLoader';
import {getSCOTsByEngagament} from '../../../../actions/scots/scotsactions';
import Ellipses from '../../EllipsesControl/EllipsesPresets';
import EllipsesControl from '../../EllipsesControl/EllipsesControl';
import {pagingOptions} from '../../../../util/uiconstants';
import {IconoirCheckCircle, IconoirInfoCircle} from '@ey-xd/motif-icon';
import MotifTextLink from '@ey/motif-react-wrapper/TextLink';

let gridApi;
const JESourceToSCOTs = () => {
	const dispatch = useDispatch();
	const engagementId = useEngagementId();
	const scots = useSelector((state) => state.scots);
	const [associatedSCOT, setAssociatedSCOT] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const rowData = useSelector((state) => getJournalSourceById(state));
	const [selectedPageSizeIndex, setSelectedPageSizeIndex] = useState(0);

	const handleGridReady = (params) => {
		gridApi = params.api;
	};

	useEffect(() => {
		let temSCOTs = [];
		scots &&
			scots?.map((m) => {
				temSCOTs.push({label: m.scotName, value: m.id, scotTypes: m.scotTypes});
			});

		setAssociatedSCOT(temSCOTs);
	}, [scots]);

	const getAllSCOTs = () => {
		dispatch(getSCOTsByEngagament(engagementId)).then(() => {
			setIsLoading(false);
		});
	};

	const fetch = useCallback(() => {
		dispatch(JournalSourceActions.getAllJournalSources(engagementId)).then(() => {
			getAllSCOTs();
		});
	}, [dispatch, engagementId]);

	useEffect(() => {
		setIsLoading(true);
		fetch();
	}, []);

	const scotHeaders = associatedSCOT.map((s) => {
		return {
			headerName: s.label,
			field: String(s.value),
			cellRenderer: 'checkMarkRenderer',
			maxWidth: 180,
			suppressMovable: true
		};
	});
	const columnDefinitions = [
		{
			headerName: labels.jeSourcesLabel,
			field: 'journalSourceName',
			cellRenderer: 'sourceNameRenderer',
			filter: true,
			pinned: 'left',
			sort: 'asc',
			width: 155,
			suppressMovable: true
		},
		...scotHeaders
	];

	const customTableHeaderRenderer = (params) => {
		return (
			<div className="customTableHeader">
				<EllipsesControl content={params.displayName} tooltip={params.displayName} noOfLines={2} />
			</div>
		);
	};
	const jeSourceNameRenderer = (params) => {
		return (
			<StyledSourceName>
				<section className="je-source-name">
					<MotifTextLink href="#" className="source-name">
						<Ellipses.Single>{params.data.journalSourceName}</Ellipses.Single>
					</MotifTextLink>
					<MotifTooltip
						contentClassName="je-source-name-tooltip"
						placement="auto"
						allowClick={false}
						allowHover={true}
						trigger={<MotifIcon src={IconoirInfoCircle} />}
						variant={'default'}
						hideCloseButton
					>
						<StyledToolTipContent>
							<p>
								<span>{labels.changeInUse}: </span>"{params.data.changeInUse}"
							</p>
							<p>
								<span>{labels.source}: </span>
								{params.data.journalSourceType ? `"${labels[params.data.journalSourceType + 'Label']}"` : 'null'}
							</p>
						</StyledToolTipContent>
					</MotifTooltip>
				</section>
			</StyledSourceName>
		);
	};

	const checkMarkRenderer = (params) => {
		const scotId = Number(params.colDef.field);
		const scotJournalSource = params.data.scotJournalSource;
		return scotJournalSource.some((s) => s.scotId === scotId) ? <StyledCheckMark src={IconoirCheckCircle} /> : '';
	};

	return (
		<StyledTable>
			<ProgressLoader show={isLoading} />
			{!isLoading && (
				<section className="je-source-to-scots-table">
					<MotifTableWrapper
						columnDefs={columnDefinitions}
						rowData={rowData}
						onGridReady={handleGridReady}
						headerHeight={60}
						rowHeight={60}
						overlayNoRowsTemplate={labels.noJournalEntrySourcesAreAvailable}
						suppressRowClickSelection
						suppressCellFocus
						animateRows
						components={{
							agColumnHeader: customTableHeaderRenderer,
							sourceNameRenderer: jeSourceNameRenderer,
							checkMarkRenderer: checkMarkRenderer
						}}
						pagination
						paginationPageSize={pagingOptions.options[selectedPageSizeIndex].toString()}
						paginationSelect={
							<>
								<span className="pagination-show">{labels.pagingShowtext}</span>
								<MotifSelect value={[selectedPageSizeIndex]} onChange={() => {}}>
									{pagingOptions.options.map((option, index) => (
										<MotifOption
											value={index}
											isSelected={index === selectedPageSizeIndex}
											onClick={() => {
												setSelectedPageSizeIndex(index);
												gridApi.paginationSetPageSize(pagingOptions.options[index]);
											}}
										>
											{option.toString()}
										</MotifOption>
									))}
								</MotifSelect>
							</>
						}
					/>
				</section>
			)}
		</StyledTable>
	);
};
export default JESourceToSCOTs;

const StyledTable = styled.section`
	height: calc(100% - var(--px-140));

	.pagination-show {
		font-size: var(--px-14);
		padding: var(--px-2) var(--px-5) 0 0;
	}

	.StyledMotifTableWrapper,
	.je-source-to-scots-table {
		height: 100%;
	}

	.motif-table {
		&.AGGridWrapperSection {
			.ag-root-wrapper {
				border: var(--px-1) solid transparent;
				background: transparent;
			}
			.ag-header-cell:not(.ag-column-resizing):hover {
				background-color: var(--neutrals-200);
			}
			.ag-header-cell:not(.ag-column-resizing) + .ag-header-cell:not(.ag-header-cell-moving):hover {
				background-color: var(--neutrals-200);
			}
			.ag-paging-panel {
				border-radius: 0 0 var(--px-12) var(--px-12);
			}
		}
		.ag-cell {
			&:focus {
				background: transparent !important;
			}
		}
		.ag-ltr {
			.ag-has-focus {
				.ag-cell-focus:not(.ag-cell-range-selected) {
					border: none;
				}
			}
		}
		.ag-root {
			${(props) => (props.rowData ? `background-color: var(--neutrals-00white);` : '')}
			&.ag-layout-auto-height {
				height: 100%;
			}
			.ag-body-horizontal-scroll {
				position: absolute;
				bottom: 0;
				padding-right: var(--px-9);
				.ag-horizontal-left-spacer {
					visibility: hidden;
				}
				.ag-scroller-corner {
					display: none;
				}
				&,
				.ag-body-horizontal-scroll-viewport {
					max-height: var(--px-10) !important;
					min-height: var(--px-10) !important;

					@media screen and (min-width: 992px) and (max-width: 1366px) {
						max-height: var(--px-14) !important;
						min-height: var(--px-14) !important;
					}
				}
				@media screen and (min-width: 992px) and (max-width: 1366px) {
					padding-right: var(--px-12);
				}
			}
			.ag-center-cols-viewport {
				&::-webkit-scrollbar {
					display: none;
				}
			}
		}
		.ag-body-horizontal-scroll-viewport,
		.customScrollbar {
			&::-webkit-scrollbar {
				height: var(--px-10);
				width: var(--px-10);
			}
			&::-webkit-scrollbar-track {
				background: var(--neutrals-200);
				height: var(--px-10);
				width: var(--px-10);
			}
			&::-webkit-scrollbar-thumb {
				background: var(--neutrals-300);
				height: var(--px-10);
				width: var(--px-10);
				cursor: pointer;
				border: 2px solid var(--neutrals-200);
				border-radius: var(--px-20);
			}
			&::-webkit-scrollbar-thumb:hover {
				background: #747480;
			}
			@media screen and (min-width: 992px) and (max-width: 1366px) {
				&::-webkit-scrollbar {
					height: var(--px-14);
					width: var(--px-14);
				}
				&::-webkit-scrollbar-track {
					background: var(--neutrals-200);
					height: var(--px-14);
					width: var(--px-14);
				}
				&::-webkit-scrollbar-thumb {
					height: var(--px-14);
					width: var(--px-14);
				}
			}
		}
		.ag-center-cols-clipper {
			margin-right: -1px;
			.ag-row {
				.ag-cell {
					.ag-react-container {
						justify-content: center;
						align-items: center;
					}
				}
			}
		}
		.ag-header {
			border: none;
			border-radius: var(--px-12) var(--px-12) 0 0;
			.ag-pinned-left-cols-container,
			.ag-pinned-left-header {
				/* box-shadow: 1px 0 4px -1px rgba(0, 0, 0, 0.25); */
				box-shadow: 2px 0 6px 0 #00000040;
				.ag-header-row {
					height: 100% !important;
					.ag-header-cell {
						padding: 0;
					}
				}
			}
			.ag-pinned-left-header,
			.ag-header-viewport,
			.ag-header-cell {
				background-color: var(--neutrals-200);
			}
			.ag-header-viewport {
				&:after {
					width: 0 !important;
				}
				.ag-header-container {
					.ag-header-row {
						height: 100% !important;
					}
					.customTableHeader {
						justify-content: center;
					}
				}
			}
			.ag-header-cell {
				.ag-react-container {
					padding: var(--px-2) var(--px-12);
					.customTableHeader {
						width: 100%;
						display: flex;
						align-items: center;
						color: var(--neutrals-900);
					}
				}
			}
		}
		.ag-body-viewport {
			margin-top: -1px;
			&.ag-layout-auto-height {
				margin-bottom: 0;
				overflow-y: auto;
				max-height: var(--px-1100);
				border-radius: 0 0 var(--px-12) var(--px-12);
				background-color: var(--neutrals-00white);
			}
			&:after {
				width: 0 !important;
			}
			.ag-pinned-left-cols-container {
				margin-left: -1px;
				box-shadow: 2px 0 6px 0 #00000040;
				.ag-cell-last-left-pinned {
					border-right: none !important;
				}
				.ag-cell {
					padding: 0;
				}
			}
			.ag-row {
				font-weight: normal;
				border: var(--px-1) solid transparent !important;
				&.ag-row-hover {
					font-weight: normal;
					background: var(--neutrals-25) !important;
					border: var(--px-1) solid transparent !important;
					& > .ag-column-hover {
						background: transparent !important;
					}
				}
				&.ag-row-first {
					font-weight: normal;
					background: var(--neutrals-00white) !important;
				}
				&.ag-row-even,
				&.ag-row-odd {
					background: var(--neutrals-00white) !important;
				}
				&.ag-row-odd {
					background: var(--neutrals-25) !important;
					&.ag-row-hover {
						background: var(--neutrals-25) !important;
					}
				}
				.ag-row-selected {
					background-color: var(--neutrals-100) !important;
					&.ag-row-even,
					&.ag-row-odd {
						background-color: var(--neutrals-100) !important;
					}
				}
			}
		}
		.ag-overlay {
			.ag-overlay-panel {
				.ag-overlay-wrapper {
					font-style: normal;
					font-weight: 300;
					font-size: var(--px-16);
					line-height: var(--px-24);
					color: var(--neutrals-300);
					.ag-overlay-no-rows-wrapper {
						border: var(--px-1) solid var(--neutrals-200);
						border-radius: 0 0 var(--px-12) var(--px-12);
					}
				}
			}
		}
		input[class^='ag-'][type='button']:focus,
		button:focus {
			box-shadow: none !important;
		}
		.motif-table-end-component-renderer {
			overflow: hidden;
			text-overflow: ellipsis;
			display: block;
		}
		.ag-cell,
		.ag-header-cell {
			font-weight: 400;
			font-size: var(--px-14);
			line-height: var(--px-22);
			padding: 0;
		}
	}

	.pagination-control-wrapper {
		align-items: center;

		.motif-select {
			height: var(--px-14) !important;
			display: flex;
			align-items: center;
			.motif-select-input {
				display: flex;
				align-items: center;
				min-height: var(--px-14) !important;
				.motif-select-input-text {
					width: auto;
				}
				.motif-select-input-arrow {
					position: static;
				}
			}
		}
		.motif-select-wrapper-options {
			margin: var(--px-4) 0;
		}
	}
`;

const StyledCheckMark = styled(MotifIcon)`
	svg {
		color: var(--green-500);
		cursor: default;
	}
`;

const StyledSourceName = styled.div`
	display: flex;
	width: 100%;
	align-items: center;
	justify-content: flex-start;
	padding: 0 var(--px-12);

	.je-source-name {
		display: flex;
		width: 100%;
		justify-content: flex-start;
		.source-name {
			max-width: 80%;
			color: var(--blue-600);
			.ellipses {
				max-width: var(--px-480);
				height: 100%;
			}
		}
		.motif-icon svg {
			height: var(--px-25) !important;
			width: var(--px-25) !important;
		}
		.motif-tooltip-wrapper {
			flex-shrink: 0;
		}
	}
`;

const StyledToolTipContent = styled.div`
padding: var(--px-2);
p{
	margin-bottom: 0;
	font-size: var(--px-12);
	padding: var(--px-2);
	/* color: var(--neutrals-700); */
	color: #585860;
	span{
		font-weight: 600;
	}
}

`;
